/**
 * Productions on the wire (PLAN §5.1, §4.1, D22): one run of a playlist (or of
 * a single rundown) in a studio.
 *
 *   - **The config snapshot (D22, D32).** A production picks a studio and a
 *     show style (the studio's `defaultShowStyleId` when it names none).
 *     Creating it resolves the pair into the production config the engine
 *     runs (`config`) and keeps a copy of both documents as they were
 *     (`snapshot`, with `studioVersion` and `showStyleVersion`). Playout runs
 *     from `config` for the production's whole life; later edits to either
 *     apply to new productions only. A production created before D32 has
 *     `showStyleId`, `showStyleVersion` and `snapshot` null: its `config` was
 *     the whole studio config, which is the same shape.
 *   - **Pinning (§4.1).** `packageHash` is the production package the process
 *     runs from, fixed at launch. Null until the supervisor pins it (never, for a child-launched one).
 *   - **Redundancy (§8, D9).** `activeNode` is the node whose process is
 *     ACTIVE, `term` bumps on every promotion. Null / 0 until a node runs it.
 *   - **Status.** `created` (not activated, or deactivated again), `active`
 *     (its playlist is activated for playout), `ended` (terminal: no more
 *     commands). `version` is the record's own optimistic-concurrency counter.
 *
 * A production lives in the studio it was created in, so its resource scope
 * for a permission check (D14) is that studio's (`studioId`, `siteId`).
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { pageLimitWireSchema, pageTokenWireSchema, PAGE_LIMIT_DESCRIPTION, PAGE_TOKEN_DESCRIPTION } from './rows.js';
import { configDocumentWireSchema, MAX_PRODUCTION_CONFIG_CHARS } from './configDocument.js';
import { showStyleConfigWireSchema } from './showStyles.js';
import { studioConfigWireSchema } from './studios.js';
import { boundedIdWireSchema, isoTimestampWireSchema, recordedPackageHashWireSchema, showStyleIdWireSchema, siteIdWireSchema, studioIdWireSchema } from './primitives.js';

/** The production config document (the resolved pair, or a pre-D32 studio config): opaque here, bounded (`configDocument.ts`). */
export const productionConfigWireSchema = configDocumentWireSchema(MAX_PRODUCTION_CONFIG_CHARS, 'production config');
export type ProductionConfigWire = z.infer<typeof productionConfigWireSchema>;

/** The two documents a production was resolved from, as they were at its creation (D32). */
export const productionSnapshotWireSchema = z.object({ showStyle: showStyleConfigWireSchema, studio: studioConfigWireSchema }).strict();

const PRODUCTION_STATUSES = ['created', 'active', 'ended'] as const;
/** Closed: what a query filters on. */
const productionStatusWireSchema = z.enum(PRODUCTION_STATUSES);
export type ProductionStatusWire = z.infer<typeof productionStatusWireSchema>;
/** Open (D37 §4.3): a production record's status as another version reads it. */
const productionStatusOpenWireSchema = openEnum(PRODUCTION_STATUSES);
export type ProductionStatusOpenWire = z.infer<typeof productionStatusOpenWireSchema>;

const userRefSchema = z.string().min(1).max(128);

/** A production without its config snapshot: what a listing shows. */
export const productionSummaryWireSchema = z
  .object({
    productionId: ulidSchema,
    studioId: studioIdWireSchema,
    /** The studio's site when the production was created (the resource scope, D14). */
    siteId: siteIdWireSchema.nullable(),
    /** What it runs: exactly one of the two is set. */
    playlistId: ulidSchema.nullable(),
    rundownId: ulidSchema.nullable(),
    /** Created for rehearsal: `activate` defaults to it, and nothing is aired for real (Sofie's rehearsal mode). */
    rehearsal: z.boolean(),
    status: productionStatusOpenWireSchema,
    /** The studio version the snapshot was taken from. */
    studioVersion: z.number().int().positive(),
    /** The show style it runs (D32); null for a production created before the split. */
    showStyleId: showStyleIdWireSchema.nullable().default(null),
    /** The show style version the snapshot was taken from; null before D32. */
    showStyleVersion: z.number().int().positive().nullable().default(null),
    /** The pinned production package (§4.1); null until the supervisor pins it. */
    packageHash: recordedPackageHashWireSchema.nullable(),
    /** The node whose production process is ACTIVE (§8); null until one runs it. */
    activeNode: boundedIdWireSchema.nullable(),
    /** The failover term (D9): bumped on every promotion. */
    term: z.number().int().nonnegative(),
    /** 1 on create, +1 per write of this record. */
    version: z.number().int().positive(),
    createdAt: isoTimestampWireSchema,
    createdBy: userRefSchema,
    endedAt: isoTimestampWireSchema.nullable(),
    endedBy: userRefSchema.nullable(),
    endReason: z.string().max(500).nullable(),
  })
  .strict();
export type ProductionSummaryWire = z.infer<typeof productionSummaryWireSchema>;

/**
 * A production with the config it runs from (the resolved pair, D22/D32) and
 * the two documents it was resolved from (null before D32).
 */
export const productionWireSchema = productionSummaryWireSchema
  .extend({ config: productionConfigWireSchema, snapshot: productionSnapshotWireSchema.nullable().default(null) })
  .strict();
export type ProductionWire = z.infer<typeof productionWireSchema>;

export const createProductionRequestWireSchema = z
  .object({
    /** Client-chosen ULID: a retried create returns the production as it is. */
    productionId: ulidSchema.optional(),
    studioId: studioIdWireSchema,
    /** The show style to run (D32); absent = the studio's `defaultShowStyleId` (`validation-failed` when it has none). */
    showStyleId: showStyleIdWireSchema.optional(),
    /** Run a playlist… */
    playlistId: ulidSchema.nullable(),
    /** …or a single rundown. Exactly one of the two. */
    rundownId: ulidSchema.nullable(),
    rehearsal: z.boolean(),
  })
  .strict()
  .refine((body) => (body.playlistId === null) !== (body.rundownId === null), {
    message: 'set exactly one of playlistId and rundownId',
    path: ['playlistId'],
  });

export const endProductionRequestWireSchema = z
  .object({
    /** Why, for the as-run log; null for none. */
    reason: z.string().max(500).nullable(),
  })
  .strict();

export const productionPathWireSchema = z.object({ productionId: ulidSchema.describe('The production’s id (a ULID).') }).strict();

export const listProductionsQueryWireSchema = z
  .object({
    studioId: studioIdWireSchema.optional().describe('Only the productions of this studio.'),
    status: productionStatusWireSchema.optional().describe('Only productions in this status.'),
    pageToken: pageTokenWireSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitWireSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
  })
  .strict();

export const productionResponseWireSchema = z.object({ production: productionWireSchema }).strict();
export const listProductionsResponseWireSchema = z
  .object({
    /** Newest first. */
    productions: z.array(productionSummaryWireSchema),
    nextPageToken: pageTokenWireSchema.nullable(),
  })
  .strict();
